import { useCallback, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { offlineFallbackCachedAt, subscribeOfflineFallbackStatus } from '../state/offline-cache.ts';

export function OfflineStatusBadge({ isOnline }: { isOnline: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const subscribe = useCallback(
    (listener: () => void) => {
      const unsubscribeFallbacks = subscribeOfflineFallbackStatus(listener);
      const unsubscribeQueries = queryClient.getQueryCache().subscribe(listener);
      return () => {
        unsubscribeFallbacks();
        unsubscribeQueries();
      };
    },
    [queryClient],
  );
  const getSnapshot = useCallback(() => {
    let oldestCachedAt: number | null = null;
    for (const query of queryClient.getQueryCache().getAll()) {
      if (query.getObserversCount() === 0) continue;
      const visibleData = [query.state.data, ...query.observers.map((observer) => observer.getCurrentResult().data)];
      for (const data of visibleData) {
        const cachedAt = offlineFallbackCachedAt(data);
        if (cachedAt !== null && (oldestCachedAt === null || cachedAt < oldestCachedAt)) {
          oldestCachedAt = cachedAt;
        }
      }
    }
    return oldestCachedAt;
  }, [queryClient]);
  const cachedAt = useSyncExternalStore(subscribe, getSnapshot, () => null);
  if (isOnline && cachedAt === null) return null;

  return (
    <span className="badge border-[color:var(--color-warning)] text-[color:var(--color-warning)]" role="status">
      {t(isOnline ? 'appLayout.cachedFallback' : 'appLayout.offline')}
    </span>
  );
}
